import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Home, X } from "lucide-react";
import api from "@/lib/api";
import { shareListing } from "@/lib/share";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { useMarket, matchesStay, marketFirst } from "@/context/LocationContext";
import { useFilters, inBudget, budgetParams } from "@/context/FiltersContext";
import { useSaves } from "@/context/SavesContext";
import { mockListings } from "@/data/listings";
import { PROVINCE_OPTIONS } from "@/data/canada_cities";

// Full province name for the honest broaden notice (e.g. "BC" -> "British
// Columbia"); falls back to the code itself if unknown.
function provinceName(code) {
  if (!code) return "your province";
  const match = PROVINCE_OPTIONS.find((p) => p.value === code);
  return match ? match.label : code;
}
import CardStack from "@/components/swipe/card_stack";
import SwipeCard from "@/components/swipe/swipe_card";
import QuotaCard from "@/components/swipe/quota_card";
import SwipesLeftModal from "@/components/gates/swipes_left_modal";
import ListingExpand from "@/components/swipe/listing_expand";
import SwipeActions from "@/components/swipe/swipe_actions";
import MatchModal from "@/components/swipe/match_modal";
import CompleteProfileGate from "@/components/profile/complete_profile_gate";
import VerifyGateModal from "@/components/gates/verify_gate_modal";
import SwipeLimitModal from "@/components/gates/swipe_limit_modal";
import ButtonGhost from "@/components/buttons/button_ghost";

function applyFilters(list, f, budget) {
  return list.filter((l) => {
    if (!inBudget(l, budget)) return false;
    if (!f) return true;
    if (f.city && f.city !== "any" && l.city !== f.city) return false;
    if (f.minPrice && l.price < Number(f.minPrice)) return false;
    if (f.maxPrice && l.price > Number(f.maxPrice)) return false;
    if (f.beds && f.beds !== "any" && l.beds < Number(f.beds)) return false;
    if (f.pets && !l.pets) return false;
    if (f.furnished && !l.furnished) return false;
    return true;
  });
}

// Peek poses: mobile stacks the next cards DIRECTLY behind the full-bleed
// top card (Tinder style — they reveal only during the drag); desktop fans
// them out to both sides. Scale per depth is owned by CardStack.
const pose = (depth, lg) =>
  depth === 0
    ? lg
      ? { x: 28, rotate: 1.6 }
      : {}
    : depth === 1
    ? lg
      ? { x: -28, rotate: -1.6 }
      : {}
    : lg
    ? { x: 28, rotate: 1.6 }
    : {};

// `pauseKeys` lets the page pause keyboard swiping while one of ITS overlays
// (the filter sheet) is open, so arrows never swipe a card hidden behind it.
export default function SwipeDeck({ filters, pauseKeys = false }) {
  const { user } = useAuth();
  const { refreshNow } = useNotifications();
  const { market, stayType } = useMarket();
  const { budget } = useFilters();
  const { ids: savedIds, toggle: toggleSave } = useSaves();
  const [cards, setCards] = useState([]);
  const [history, setHistory] = useState([]);
  // { id, dir } — a restored card reverse-swipes in from its exit vector.
  const [undone, setUndone] = useState(null);
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileGate, setProfileGate] = useState(null); // null | { missing: [] }
  // Identity gates: the backend refuses swipes with 403 VERIFICATION_REQUIRED
  // (unverified account) or 403 SWIPE_LIMIT (free daily cap reached).
  const [verifyGate, setVerifyGate] = useState(false);
  const [limitGate, setLimitGate] = useState(false);
  // Slim "viewer mode" notice for unverified accounts; dismissible per visit.
  const [viewerNoticeDismissed, setViewerNoticeDismissed] = useState(false);
  // Daily swipe allowance for FREE renters: fetched once from /swipes/quota,
  // then decremented locally per real swipe so the "N left" numbers shown in
  // the load popup and the every-fifth-card reminder stay live without
  // re-polling. null until known; {unlimited:true} for Plus/admin/listers.
  const [quota, setQuota] = useState(null);
  const quotaRef = useRef(null);
  const [quotaModal, setQuotaModal] = useState(false);
  // Count of REAL swipes this visit (quota reminder cards never count).
  const realSwipesRef = useRef(0);
  // The Tinder-style profile expand: { listing, photo } while open. The
  // listing is snapshotted at open time so a deck advance never swaps the
  // popup content mid collapse.
  const [expand, setExpand] = useState(null);
  // Honest geographic broadening notice: set when the selected city was sparse
  // and the deck was topped up from the same province. { province } or null.
  const [broadened, setBroadened] = useState(null);
  const navigate = useNavigate();
  // Persistent stack handle: buttons, keyboard and the popup all swipe the
  // live top card through it (same fly-out as a real drag).
  const stackRef = useRef(null);

  // Fetch today's allowance once per visit for signed in, non demo users.
  // The popup shows once per day (localStorage date key) so returning to the
  // page within the same day never nags twice.
  useEffect(() => {
    if (!user || user.demo) return undefined;
    let alive = true;
    api
      .get("/swipes/quota")
      .then((res) => {
        if (!alive || !res.data || res.data.unlimited) return;
        quotaRef.current = res.data;
        setQuota(res.data);
        const today = new Date().toISOString().slice(0, 10);
        const key = "swiprent_swipes_left_seen";
        if (localStorage.getItem(key) !== today) {
          localStorage.setItem(key, today);
          setQuotaModal(true);
        }
      })
      .catch(() => {}); // offline or guest: no counter, deck still works
    return () => { alive = false; };
  }, [user]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // The budget filter is applied on the server (price_min/price_max) AND on
    // the client, so the mock fallback path respects it too.
    const prep = (list) =>
      marketFirst(applyFilters(list, filters, budget).filter((l) => matchesStay(l, stayType)), market?.city);
    api
      .get("/swipes/deck", {
        params: {
          type: "listings",
          stay_type: stayType,
          city: market?.city,
          province: market?.province,
          ...budgetParams(budget),
        },
      })
      .then((res) => {
        if (!alive) return;
        const data = Array.isArray(res.data) ? res.data : res.data?.deck || res.data?.items;
        // Honest broaden notice: only when the server broadened AND the wider
        // tier actually contributed cards (never fabricate). Two flavors:
        // province fill ("more across British Columbia") and the nationwide
        // fallback ("more across Canada") for when the whole province is dry.
        const meta = !Array.isArray(res.data) ? res.data : null;
        setBroadened(
          meta?.broadened && (meta.province_count > 0 || meta.country_count > 0)
            ? {
                province: meta.province,
                nationwide: !(meta.province_count > 0) && meta.country_count > 0,
              }
            : null
        );
        // Mock listings are a DEMO-ONLY fallback. Real (non-demo) users with
        // an empty deck get the honest "No homes yet" empty state — never fake
        // homes. Demo sessions seed the mock deck so browsing stays demoable.
        // (The __E2E_NO_MOCK__ escape hatch lets an integration test exercise
        // the real empty-deck path in a demo build; it is undefined in prod.)
        const allowMock = user?.demo && typeof window !== "undefined" && !window.__E2E_NO_MOCK__;
        const fallback = allowMock ? mockListings : [];
        setCards(prep(data?.length ? data : fallback));
      })
      .catch(() => {
        if (!alive) return;
        setBroadened(null);
        const allowMock = user?.demo && typeof window !== "undefined" && !window.__E2E_NO_MOCK__;
        setCards(prep(allowMock ? mockListings : []));
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [filters, stayType, market?.city, market?.province, budget, user?.demo]);

  // Called by the physics hook at fly-out START (optimistic), so the next
  // card is interactive immediately. Returning false vetoes the fly-out and
  // the card springs back while the gate modal opens.
  const swipe = useCallback(
    (action, listing) => {
      if (!listing) return false;
      // Quota reminder card: dismissing it is FREE. No API call, no history
      // entry, no gate checks — it just flies out and the deck moves on.
      if (listing.__quota) {
        setUndone(null);
        setCards((c) => c.filter((x) => x.id !== listing.id));
        return true;
      }
      // Incomplete profiles cannot swipe: open the finish-your-profile gate.
      if (user && !user.demo && user.profile_complete === false) {
        setProfileGate({ missing: user.missing_fields || [] });
        return false;
      }
      setUndone(null);
      setHistory((h) => [...h, { listing, action }]);
      // Live allowance: decrement locally per real swipe, and after every
      // 5th real swipe slot a reminder card in as the NEXT card. The
      // reminder itself never counts (see the __quota branch above).
      const q = quotaRef.current;
      realSwipesRef.current += 1;
      let nextRemaining = null;
      if (q && !q.unlimited) {
        nextRemaining = Math.max(0, (q.remaining ?? 0) - 1);
        quotaRef.current = { ...q, remaining: nextRemaining };
        setQuota(quotaRef.current);
      }
      const remind = q && !q.unlimited && realSwipesRef.current % 5 === 0;
      setCards((c) => {
        const rest = c.filter((x) => x.id !== listing.id);
        if (remind && rest.length > 0) {
          return [{ id: `quota_${realSwipesRef.current}`, __quota: true,
                    remaining: nextRemaining, limit: q.limit }, ...rest];
        }
        return rest;
      });
      api
        .post("/swipes", { target_id: listing.id, target_type: "listing", action })
        .then((res) => {
          // A swipe can create a match (badge!) the instant it lands, so pull
          // fresh notification counters instead of waiting for the next poll.
          refreshNow();
          if (res.data?.match) setMatch({ listing, matchId: res.data.match_id || res.data.match?.id || "m1" });
        })
        .catch((error) => {
          // A refused swipe never counts: roll the local allowance back so
          // the "N left" numbers stay truthful.
          const rollback = (zero = false) => {
            realSwipesRef.current = Math.max(0, realSwipesRef.current - 1);
            const cur = quotaRef.current;
            if (cur && !cur.unlimited) {
              quotaRef.current = { ...cur,
                remaining: zero ? 0 : (cur.remaining ?? 0) + 1 };
              setQuota(quotaRef.current);
            }
          };
          // Backend can refuse the swipe until the profile is complete.
          if (error?.response?.status === 403 && error.response.data?.code === "PROFILE_INCOMPLETE") {
            rollback();
            setCards((c) => (c.some((x) => x.id === listing.id) ? c : [listing, ...c]));
            setUndone({ id: listing.id, dir: action === "pass" ? -1 : 1 });
            setProfileGate({ missing: error.response.data?.missing || [] });
            return;
          }
          // Identity-unverified accounts browse only: restore the card and
          // open the verification gate instead of losing the swipe silently.
          if (error?.response?.status === 403 && error.response.data?.code === "VERIFICATION_REQUIRED") {
            rollback();
            setCards((c) => (c.some((x) => x.id === listing.id) ? c : [listing, ...c]));
            setUndone({ id: listing.id, dir: action === "pass" ? -1 : 1 });
            setVerifyGate(true);
            return;
          }
          // Free daily cap reached: restore the card and open the Renter
          // Plus paywall — the swipe was never counted server side.
          if (error?.response?.status === 403 && error.response.data?.code === "SWIPE_LIMIT") {
            rollback(true); // server says the day is spent: show 0 left
            setCards((c) => (c.some((x) => x.id === listing.id) ? c : [listing, ...c]));
            setUndone({ id: listing.id, dir: action === "pass" ? -1 : 1 });
            setLimitGate(true);
            return;
          }
          if (error?.response) return;
          // Pretend matches are DEMO ONLY: an offline demo session still
          // celebrates on some likes so the flow is demoable, but a real user
          // must never see a match the server did not create.
          if (user?.demo && action !== "pass" && Math.random() < 0.4) setMatch({ listing, matchId: "m1" });
        });
    },
    [user, refreshNow]
  );

  function undo() {
    if (!history.length) {
      toast.info("Nothing to undo yet.");
      return;
    }
    const last = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setCards((c) => (c.some((x) => x.id === last.listing.id) ? c : [last.listing, ...c]));
    setUndone({ id: last.listing.id, dir: last.action === "pass" ? -1 : 1 });
    // Undo on the server too: a withdrawn like must be removed so the lister
    // no longer sees it in their likes/applicants (best effort — the card is
    // already back on the deck locally). Demo sessions have no backend.
    if (!user?.demo && last.listing?.id) {
      api.delete(`/swipes/listing/${last.listing.id}`).catch(() => {});
    }
  }

  const top = cards[0];

  // Viewer mode: signed in but identity unverified (and not a demo session).
  // Browsing works; swiping, matching, messaging and payments are gated.
  const showViewerNotice =
    !!user && !user.demo && !user.verified && user.kyc_status !== "approved" && !viewerNoticeDismissed;

  // Live top-card id, readable from DELAYED callbacks (the expand popup
  // collapses first and fires its action ~200ms later, when render-time
  // closures may already be one deck advance stale).
  const topIdRef = useRef(null);
  useEffect(() => {
    topIdRef.current = cards[0]?.id ?? null;
  }, [cards]);

  // Buttons run the exact same fly-out as a drag via the stack handle.
  // Returns false when a gate vetoed it (the expand popup needs to know).
  const swipeTop = (action) => stackRef.current?.swipeTop(action) ?? false;

  // The expand popup must act on THE LISTING IT SHOWS. If the deck advanced
  // while the popup was open (or during its collapse), swiping the new top
  // card would like the wrong home, so we no-op with an honest toast instead.
  const expandAction = (action, shown) => {
    if (!shown || topIdRef.current !== shown.id) {
      toast.info("The deck moved on, so nothing was swiped.");
      return false;
    }
    return swipeTop(action);
  };

  // Keyboard swiping mirrors the landing deck: left arrow passes, right
  // arrow likes, up arrow or U undoes the last swipe. Paused while any
  // overlay (filter sheet, expand popup, match or profile gate) is open so
  // arrows never swipe a card the user cannot see.
  useEffect(() => {
    function onKey(e) {
      if (pauseKeys || expand || match || profileGate || verifyGate || limitGate || quotaModal || loading) return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "ArrowRight") { e.preventDefault(); swipeTop("like"); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); swipeTop("pass"); }
      else if (e.key === "ArrowUp" || e.key === "u" || e.key === "U") { e.preventDefault(); undo(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pauseKeys, expand, match, profileGate, verifyGate, limitGate, quotaModal, loading, history]);

  // The bookmark SAVES the home — it never advances the deck. Quota
  // reminder cards are not homes, so save and share both no-op on them.
  const saveListing = (listing) => {
    if (!listing || listing.__quota || !user) return;
    toggleSave(listing);
  };
  const shareTop = (listing) => {
    if (!listing || listing.__quota) return;
    shareListing(listing);
  };

  // Share opens the phone's native share sheet; never advances the deck.
  // (Shared helper: @/lib/share, also used by the landing deck.)

  return (
    <div className="relative flex h-full w-full flex-col items-center lg:h-auto">
      {/* Deck notices share one slot: on mobile they float over the
          full-bleed photo below the filter pills (top-28 clears the floating
          pills at top right); on desktop they sit statically above the card.
          Emerald hairline style, non blocking, never fabricated. */}
      {!loading && (showViewerNotice || (broadened && cards.length > 0)) && (
        <div className="absolute inset-x-4 top-28 z-30 space-y-2 lg:static lg:inset-auto lg:mb-4 lg:w-full lg:max-w-[min(66vw,1000px)]">
          {/* Viewer mode notice: unverified accounts can browse everything
              but must pass the one time ID check to swipe and match. */}
          {showViewerNotice && (
            <div className="relative rounded-xl border-l-2 border-brand-primary bg-night-tint px-3.5 py-2 pr-9 shadow-ambient lg:shadow-none">
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => setViewerNoticeDismissed(true)}
                className="absolute right-1.5 top-1.5 rounded-full p-1 text-night-sub transition hover:text-night-text"
              >
                <X size={14} />
              </button>
              <p className="text-xs font-semibold text-night-text">You are browsing as a viewer.</p>
              <p className="mt-0.5 text-xs text-night-sub">
                Verify your identity to swipe and match.{" "}
                <Link to="/profile" className="font-semibold text-brand-primary underline underline-offset-2">
                  Verify
                </Link>
              </p>
            </div>
          )}
          {/* Honest geographic broadening notice: shown only when the
              selected city was sparse and the deck was topped up from the
              same province (or nationwide). */}
          {broadened && cards.length > 0 && (
            <div className="rounded-xl border-l-2 border-brand-primary bg-night-tint px-3.5 py-2 shadow-ambient lg:shadow-none">
              <p className="text-xs font-semibold text-night-text">
                Not many homes in {market?.city} yet
              </p>
              <p className="mt-0.5 text-xs text-night-sub">
                Showing more across {broadened.nationwide ? "Canada" : provinceName(broadened.province)}.
              </p>
            </div>
          )}
        </div>
      )}
      {/* TINDER-STYLE FULL BLEED on mobile: the card fills every pixel between
          the top nav and the bottom tab bar — no aspect cap, no side gutters,
          no white frame. Desktop keeps one big landscape card with the next
          two cards peeking out from both sides. */}
      <div className="relative min-h-0 w-full flex-1 lg:h-[min(72vh,720px)] lg:min-h-[420px] lg:w-[min(66vw,1000px)] lg:max-w-none lg:flex-none">
        {loading ? (
          <div className="flex h-full items-center justify-center bg-night-surface lg:rounded-3xl lg:border lg:border-night-line">
            <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-night-line border-t-brand-primary" />
          </div>
        ) : cards.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 bg-night-surface p-8 text-center lg:rounded-3xl lg:border lg:border-night-line">
            <Home size={28} strokeWidth={1.6} className="text-night-text" />
            <div>
              <h3 className="text-lg font-semibold text-night-text">You are all caught up</h3>
              <p className="mt-1 text-sm text-night-sub">New homes land every day. Widen your filters or explore the map.</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {/* WHY: the action row unmounts with the last card, so without
                  this button the final swipe could never be undone. */}
              {history.length > 0 && <ButtonGhost onClick={undo}>Undo last swipe</ButtonGhost>}
              <ButtonGhost onClick={() => navigate("/map")}>Open the map</ButtonGhost>
            </div>
          </div>
        ) : (
          <CardStack
            ref={stackRef}
            deck={cards}
            onSwipe={swipe}
            pose={pose}
            undone={undone}
            demoPreview
            veilClass="rounded-none lg:rounded-3xl"
            renderCard={(l, props) =>
              l.__quota ? (
                <QuotaCard edge item={l} {...props} />
              ) : (
                <SwipeCard edge listing={l} onExpand={(photo) => setExpand({ listing: l, photo })} {...props} />
              )
            }
          />
        )}

        {/* Mobile: the action row floats ON the photo near its bottom edge
            (classic Tinder). pointer-events sit on the buttons only so the
            photo underneath stays draggable / tappable. Desktop keeps the
            row beneath the card. */}
        {!loading && cards.length > 0 && (
          <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 lg:hidden">
            <SwipeActions
              disabled={!top}
              onUndo={undo}
              onPass={() => swipeTop("pass")}
              onSave={() => saveListing(top)}
              saved={!!top && savedIds.has(top.id)}
              onLike={() => swipeTop("like")}
              onShare={() => shareTop(top)}
            />
          </div>
        )}
      </div>

      <div className="mt-5 hidden w-full max-w-[400px] lg:mt-7 lg:block">
        {/* Buttons run the exact same fly-out as a drag via the stack handle;
            the bookmark saves without advancing */}
        <SwipeActions
          disabled={!top}
          onUndo={undo}
          onPass={() => swipeTop("pass")}
          onSave={() => saveListing(top)}
          saved={!!top && savedIds.has(top.id)}
          onLike={() => swipeTop("like")}
          onShare={() => shareTop(top)}
        />
      </div>

      {/* Tinder-style profile expand — likes and nopes inside it run the
          exact same deck swipe (mounted before the modals so gates paint above) */}
      <ListingExpand
        open={!!expand}
        listing={expand?.listing}
        initialPhoto={expand?.photo || 0}
        onClose={() => setExpand(null)}
        onAction={expandAction}
      />

      <MatchModal open={!!match} listing={match?.listing} matchId={match?.matchId} onClose={() => setMatch(null)} />
      <CompleteProfileGate
        open={!!profileGate}
        onClose={() => setProfileGate(null)}
        missing={profileGate?.missing || []}
        role={user?.role}
      />
      <VerifyGateModal open={verifyGate} onClose={() => setVerifyGate(false)} />
      <SwipeLimitModal open={limitGate} onClose={() => setLimitGate(false)} />
      <SwipesLeftModal
        open={quotaModal}
        remaining={quota?.remaining ?? 0}
        limit={quota?.limit}
        onClose={() => setQuotaModal(false)}
      />
    </div>
  );
}
